import React, { useEffect, useRef } from 'react';
import { showToast } from '@repo/ui';
import { useBulkJobPolling } from '../queries';

interface BulkJobProgressTrackerProps {
  jobId: string;
  endpoint?: 'juniper' | 'juniper-trr-transactions';
  onDone: (jobId: string) => void;
}

const ACTION_LABEL: Record<string, string> = {
  send: 'Sending Emails',
  release: 'Releasing',
  regenerate_pdf: 'Regenerating PDFs',
  reprocess: 'Refreshing from Clio',
  void: 'Voiding',
  delete: 'Deleting',
  mark_completed: 'Marking Completed',
  refresh: 'Refreshing from Clio',
  retry_last_event: 'Retrying Last Events',
  consolidate: 'Consolidating PDFs',
};

/**
 * Tracks a single in-flight bulk job. Polls until terminal (or the
 * `useBulkJobPolling` ~35-minute budget runs out), shows a small progress pill
 * with processed/total + a percentage, and fires a single completion toast on
 * terminal. Calls `onDone(jobId)` once after the terminal toast so the parent
 * can remove the tracker from its list.
 *
 * Renders inline (no portal) — the parent decides placement.
 */
export const BulkJobProgressTracker: React.FC<BulkJobProgressTrackerProps> = ({
  jobId,
  endpoint,
  onDone,
}) => {
  // Guard so the terminal-toast + onDone fires exactly once even if the
  // polling hook resolves with a terminal status mid-render or React's strict
  // mode double-invokes the effect.
  const doneRef = useRef(false);

  const { data: job } = useBulkJobPolling(jobId, {
    endpoint,
    onTerminal: (j) => {
      if (doneRef.current) return;
      doneRef.current = true;
      const total = j.totalCount;
      const done = j.processedCount;
      const failed = j.failedCount;
      const ineligible = j.ineligibleCount;
      const actionLabel = ACTION_LABEL[j.action] || j.action;

      if (j.status === 'failed' && done === 0) {
        showToast.error(`${actionLabel} failed`);
      } else if (failed > 0 && done > 0) {
        showToast.warning(
          `${actionLabel}: ${done}/${total} done${failed ? `, ${failed} failed` : ''}${ineligible ? `, ${ineligible} ineligible` : ''}`,
        );
      } else if (done > 0) {
        showToast.success(
          `${actionLabel}: ${done}/${total} complete${ineligible ? ` (${ineligible} ineligible)` : ''}`,
        );
      } else if (ineligible > 0) {
        showToast.info(`${actionLabel}: ${ineligible} ineligible, nothing processed`);
      } else {
        showToast.info(`${actionLabel} complete`);
      }

      // Surface the consolidated-print download URL inline so the user gets it
      // immediately from the tracker rather than via a separate toast hop.
      if (j.action === 'consolidate' && j.downloadUrl) {
        window.open(j.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    },
  });

  // After terminal + toast, drop the tracker from the parent's list. Doing it
  // in an effect (not the onTerminal callback) avoids setState-during-render.
  useEffect(() => {
    if (!job) return;
    if (job.status === 'complete' || job.status === 'failed') {
      const t = setTimeout(() => onDone(jobId), 1500);
      return () => clearTimeout(t);
    }
  }, [job?.status, jobId, onDone]);

  if (!job) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs rounded-full">
        <Spinner />
        <span>Queuing…</span>
      </div>
    );
  }

  const done = job.processedCount + job.failedCount + job.ineligibleCount;
  const total = job.totalCount || 1;
  const pct = Math.min(100, Math.round((done / total) * 100));
  const actionLabel = ACTION_LABEL[job.action] || job.action;
  const isTerminal = job.status === 'complete' || job.status === 'failed';
  const failed = job.failedCount;

  return (
    <div
      className={
        'inline-flex items-center gap-2 px-3 py-1 text-xs rounded-full ' +
        (job.status === 'failed'
          ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200'
          : isTerminal
            ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200'
            : 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200')
      }
      title={`Job ${job.jobId.slice(0, 8)}…`}
    >
      {!isTerminal && <Spinner />}
      <span>
        {actionLabel}: {done}/{total} ({pct}%)
        {failed > 0 && (
          <span className="ml-1 text-red-700 dark:text-red-300">· {failed} failed</span>
        )}
      </span>
    </div>
  );
};

const Spinner = () => (
  <svg
    className="animate-spin h-3 w-3"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);
